import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarClock, AlertCircle } from "lucide-react";

function formatItDate(iso) {
  if (!iso) return "";
  const p = iso.slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

function calcExtension(oldIso, newIso, monthlyCost) {
  if (!oldIso || !newIso) return null;
  const old = new Date(oldIso);
  const nw = new Date(newIso);
  if (nw <= old) return { months: 0, residual: 0, decimal: 0, amount: 0 };
  // Calendar months
  let months = (nw.getFullYear() - old.getFullYear()) * 12 + (nw.getMonth() - old.getMonth());
  let anniversary = new Date(old);
  anniversary.setMonth(anniversary.getMonth() + months);
  if (anniversary > nw) {
    months -= 1;
    anniversary = new Date(old);
    anniversary.setMonth(anniversary.getMonth() + months);
  }
  const residual = Math.round((nw - anniversary) / (1000 * 60 * 60 * 24));
  const decimal = months + residual / 30;
  const amount = monthlyCost > 0 ? monthlyCost * decimal : 0;
  return {
    months,
    residual,
    decimal: Math.round(decimal * 10000) / 10000,
    amount: Math.round(amount * 100) / 100,
  };
}

export function ExtendReleaseDialog({ open, oldDate, newDate, monthlyCost, onConfirm, onCancel, loading }) {
  const [calc, setCalc] = useState(null);

  useEffect(() => {
    if (open && oldDate && newDate) {
      setCalc(calcExtension(oldDate, newDate, monthlyCost || 0));
    }
  }, [open, oldDate, newDate, monthlyCost]);

  if (!open || !calc) return null;

  const willCreateProforma = calc.months >= 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid="extend-release-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Outfit']">
            <CalendarClock className="w-5 h-5 text-amber-500" />
            Spostare la scadenza di Riconsegna?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3 p-3 rounded-sm bg-zinc-50">
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wide">Data attuale</div>
              <div className="font-medium" data-testid="extend-old-date">{formatItDate(oldDate)}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wide">Nuova data</div>
              <div className="font-medium text-[#002FA7]" data-testid="extend-new-date">{formatItDate(newDate)}</div>
            </div>
          </div>

          {willCreateProforma ? (
            <div className="p-3 rounded-sm border border-emerald-200 bg-emerald-50 space-y-1.5">
              <div className="flex items-center gap-2 text-emerald-800 font-medium">
                <CalendarClock className="w-4 h-4" />
                Verrà creata una proforma di estensione
              </div>
              <div className="text-emerald-700 text-xs">
                Periodo aggiuntivo: <strong data-testid="extend-period">{calc.months} mese/i e {calc.residual} giorno/i</strong>
                {" "}(equivalenti a {calc.decimal.toFixed(2)} mesi)
              </div>
              <div className="text-emerald-700 text-xs">
                Costo mensile deposito: {(monthlyCost || 0).toFixed(2)} €
              </div>
              <div className="text-emerald-900 text-base font-semibold pt-1" data-testid="extend-amount">
                Importo proposto: {calc.amount.toFixed(2)} € + IVA
              </div>
              <div className="text-emerald-600 text-[11px] italic">
                La proforma sarà creata in <strong>BOZZA</strong> e potrai modificarla (anche applicando uno sconto) prima di emetterla.
              </div>
            </div>
          ) : (
            <div className="p-3 rounded-sm border border-amber-200 bg-amber-50 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-amber-800 text-xs">
                Lo spostamento è inferiore a 1 mese calendario ({calc.months === 0 && calc.residual > 0 ? `${calc.residual} giorni` : "0 giorni"}).
                Verrà aggiornata <strong>solo la scadenza di pagamento</strong>; nessuna proforma di estensione verrà creata.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onCancel} disabled={loading} data-testid="extend-cancel-btn">
            Annulla
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="bg-[#002FA7] hover:bg-[#002482] rounded-sm"
            data-testid="extend-confirm-btn"
          >
            {loading ? "Applico..." : (willCreateProforma ? "Conferma e crea proforma" : "Conferma spostamento")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
